import { useState } from 'react'
import styled from 'styled-components'

import { media, mediaMax } from 'styles/media'

import { FixedColumnsMasonry } from 'components/landing/MasonryImages'
import {
  heightLarge as navbarHeightLarge,
  heightSmall as navbarHeightSmall,
  navbarSizeBreakpoint,
} from 'layout/Navbar/constants'
import SearchForm from './SearchForm'
import { Country, State, PrimaryCity } from './types'
import {
  images as defaultImages,
  imagesNZ,
  imagesWA,
  imagesOR,
} from './content'

const BREAKPOINT = 'lg' as const

const OSX_SAFARI_TOOLBAR_HEIGHT_PX = 40

const MobileOnly = styled.div`
  ${media[BREAKPOINT]`
    display: none;
  `}
`

const DesktopOnly = styled.div`
  ${mediaMax[BREAKPOINT]`
    display: none;
  `}
`

const NormalizingWrapper = styled.div`
  width: 100vw;
`

const OversizeWrapper = styled.div`
  position: relative;
  width: 115%;
  transform: translateY(-25%);
  margin-bottom: -25%; /* to clean up extra document space left by transforming upwards */

  ${media[BREAKPOINT]`
    width: 50%;
    transform: translateY(-15%);
    margin-bottom: -20%; /* to clean up extra document space left by transforming upwards */
  `}

  ${NormalizingWrapper} {
    margin: 0 auto;
  }
`

const Title = styled.h1`
  color: ${({ theme }) => theme?.['-ds-core-300']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xl']?.['font-size']?.value};
  font-weight: 500;
  margin-bottom: 16px;

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-xxxl']?.['font-size']?.value};
  `}
`

const Subtitle = styled.p`
  color: ${({ theme }) => theme?.['-ds-core-300']?.value};
  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-sm']?.['font-size']?.value};
  font-weight: 300;
  margin-bottom: 24px;

  ${media.md`
    font-size: ${({ theme }) =>
      theme?.['-ds-font-size-lg']?.['font-size']?.value};
    margin-bottom:48px;
  `}
`

const IntroWrapper = styled.div`
  ${media[BREAKPOINT]`
    width: 50%;
  `}
`

const IntroSection = styled.div`
  padding: 30px;
  margin: 0 auto;
  max-width: 600px;
`
const Outer = styled.div`
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;

  max-height: 1000px;
  height: calc(
    100vh - ${navbarHeightSmall}px - ${OSX_SAFARI_TOOLBAR_HEIGHT_PX}px
  );

  ${media[navbarSizeBreakpoint]`
    height: calc(100vh - ${navbarHeightLarge}px);
  `}

  ${media[BREAKPOINT]`
    flex-direction: row-reverse;
    justify-content: unset;
  `}

  background: ${({ theme }) => theme?.['-ds-core-50']?.value};
`

type Images = Array<{ large: string; medium: string }>

type MasonryProps = { images: Images; preload?: boolean }

const Masonry = ({ images, preload }: MasonryProps) => {
  /*
    Mobile and desktop masonry variants differ in the number of images per column.

    Both mobile and desktop markup will be written to the DOM, and toggled with the `display` css property.

    This pattern is useful because it allows server-side rendering to prepare both sets of markup, and the
    the user to be immediately shown the right version when their browser parses the html & css.

    Switching by breakpoint in the JS execution context will mean server-side rendering has to prepare either one
    variant or the other -- if the client then decides to swith to the other variant, the user will experience a
    jarring layout shift.
  */
  return (
    <>
      <MobileOnly>
        <FixedColumnsMasonry
          images={images}
          columnsCount={3}
          imagesPerColumn={{
            1: 3,
            2: 3,
            3: 3,
          }}
          gutter={8}
          preload={preload}
        />
      </MobileOnly>
      <DesktopOnly>
        <FixedColumnsMasonry
          images={images}
          columnsCount={3}
          imagesPerColumn={{
            1: 3,
            2: 3,
            3: 4,
          }}
          gutter={16}
          preload={preload}
        />
      </DesktopOnly>
    </>
  )
}

const chooseMasonryImages = (marketCode: GQL_MarketCode): Images => {
  switch (marketCode) {
    case 'NZ':
      return imagesNZ
    case 'WA':
      return imagesWA
    case 'OR':
      return imagesOR
    default:
      return defaultImages
  }
}

type HeaderProps = {
  images?: Images
  country: Country
  countries: Country[]
  states: State[]
  marketCode: GQL_MarketCode
  primaryCity: PrimaryCity
}

const Header = ({
  images: specifiedImages,
  country,
  countries,
  marketCode,
  primaryCity,
  states,
}: HeaderProps) => {
  // Locked to the first render: the post-hydration market swap must not
  // reflash the hero image grid.
  const [masonryImages] = useState(
    () => specifiedImages || chooseMasonryImages(marketCode),
  )

  return (
    <Outer className="qa-home-header">
      <OversizeWrapper>
        <Masonry images={masonryImages} preload />
      </OversizeWrapper>

      <IntroWrapper>
        <IntroSection>
          <Title>Book a short nature escape</Title>
          <Subtitle>Find stays and adventures through travel stories</Subtitle>
          <SearchForm
            marketCode={marketCode}
            country={country}
            countries={countries}
            states={states}
            primaryCity={primaryCity}
          />
        </IntroSection>
      </IntroWrapper>
    </Outer>
  )
}

export default Header
